import type { FormEvent } from 'react'
import { useEffect, useRef, useState } from 'react'

import { errorToast, Spinner } from '@blattnercompany/ui'
import {
  Building2,
  CalendarCheck,
  Hash,
  Mail,
  MapPin,
  NotebookPen,
  Package,
  Sparkles,
  Truck,
  UserRound,
  X,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import type {
  DeliveryCreate,
  DeliveryDto,
  DeliveryUpdate,
} from '@/services/deliveryService'
import { isAwaitingArrival, isTerminalStage } from '@/services/deliveryService'
import { logger } from '@/utils/logger'
import {
  dayKeyOf,
  toClockTime,
  toDayKey,
  withClockTime,
} from '@/utils/scheduleTime'

import './DeliveryEditDialog.css'

const log = logger.scoped('DeliveryEditDialog')

/** The underlying error's own message, for the toast's detail line --
    falls back to the raw value for whatever a service might reject with
    that isn't an Error. */
function errorDetail(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

/**
 * The full-record editor behind every View / Edit button -- and, with no
 * delivery given, the Add form behind the page's Add delivery button.
 *
 * Layout follows the order-vs-shipment split: the *order identity* (vendor,
 * Blattner PO) is the immutable header — purchasing fixed those the day the
 * PO was cut. The *shipment execution* (carrier, dates, BOL, who meets it,
 * where it goes) is the form — that is what churns when the vendor's
 * shipping desk rebooks (carrier swap means a new BOL; the PO never moves).
 *
 * In add mode the identity is not yet known, so it is entered rather than
 * shown: the header becomes two fields. Everything the SIS types stays text
 * -- vendor, carrier, PO -- with suggestions from what is already on the
 * board, because a hand-arranged load is exactly the case where the vendor
 * may not be in any list yet. Lookups plug into the same controls later.
 *
 * This is also the one place a delivery may cross days — the rail popover
 * deliberately clamps to the delivery's own day and routes here.
 *
 * Terminal deliveries open read-only: the record is closed, View is literal.
 */

type Props = {
  /** The record to edit. Absent = add mode: a blank form for a hand-entered
      delivery, identity fields included. */
  delivery?: DeliveryDto
  /** Suggestions for the add form's free-text fields, derived from live data
      until real directory + Site Setup sources exist. */
  carriers: string[]
  vendors: string[]
  /** Selectable receivers / yards -- selects in edit mode, suggestions in
      add mode, where the lists may still be empty on a new project. */
  receivers: string[]
  destinations: string[]
  /** Awaited so the dialog can stay open -- with every value intact -- and
      show the same loading/error state as Add/Cancel until the save either
      lands or fails. Not optimistic: see handleSaveEdit's own comment. */
  onSave: (id: string, changes: DeliveryUpdate) => Promise<void>
  /** Awaited by the add form so it can stay open -- with every value intact
      -- until the save either lands or fails. */
  onCreate: (input: DeliveryCreate) => Promise<void>
  /** Awaited by the Cancel panel for the same reason -- see `onCreate`.
      `edits` are the pending changes the user opted to keep, sent in the
      same write as the cancel. */
  onCancelDelivery: (
    id: string,
    reason: string,
    edits?: DeliveryUpdate,
  ) => Promise<void>
  onClose: () => void
}

/** Which body the dialog shows -- Cancel is a drill-in step, not a separate
    modal, so it swaps in place rather than stacking on top. */
type Panel = 'edit' | 'cancel'

type RequiredField =
  'vendor' | 'bpo' | 'date' | 'eta' | 'destination' | 'material'

/** Inline messages, in the form's reading order so focus lands on the
    topmost problem. */
const REQUIRED: { field: RequiredField; id: string; message: string }[] = [
  { field: 'vendor', id: 'edit-vendor', message: 'Enter the vendor.' },
  { field: 'bpo', id: 'edit-bpo', message: 'Enter the Blattner PO number.' },
  { field: 'date', id: 'edit-date', message: 'Choose a delivery date.' },
  { field: 'eta', id: 'edit-eta', message: 'Choose an ETA.' },
  {
    field: 'destination',
    id: 'edit-destination',
    message: 'Enter where the load is going.',
  },
  {
    field: 'material',
    id: 'edit-material',
    message: 'Describe what is on the truck.',
  },
]

const selectClassName =
  'h-9 w-full rounded-md border border-(--border-strong) bg-(--white) px-2.5 text-sm text-(--fg1)'

const labelClassName =
  'grid gap-1 text-xs font-bold tracking-wide text-(--fg2) uppercase'

function DeliveryEditDialog({
  delivery,
  carriers,
  vendors,
  receivers,
  destinations,
  onSave,
  onCreate,
  onCancelDelivery,
  onClose,
}: Props) {
  const mode = delivery ? 'edit' : 'add'
  const readOnly = delivery ? isTerminalStage(delivery.stage) : false

  const [vendor, setVendor] = useState(delivery?.vendor ?? '')
  const [bpo, setBpo] = useState(delivery?.bpoNumber ?? '')
  const [supplierOrder, setSupplierOrder] = useState(
    delivery?.supplierOrderNumber ?? '',
  )
  const [carrier, setCarrier] = useState(delivery?.carrier ?? '')
  // A new delivery is for today until told otherwise -- the field default.
  const [date, setDate] = useState(
    delivery ? toDayKey(delivery.scheduledFor) : dayKeyOf(new Date()),
  )
  const [eta, setEta] = useState(
    delivery ? toClockTime(delivery.scheduledFor) : '',
  )
  const [bol, setBol] = useState(delivery?.bolNumber ?? '')
  const [receiver, setReceiver] = useState(delivery?.driver ?? '')
  const [destination, setDestination] = useState(delivery?.destination ?? '')
  const [material, setMaterial] = useState(delivery?.material ?? '')
  const [note, setNote] = useState(delivery?.note ?? '')
  const [callConfirmed, setCallConfirmed] = useState(
    delivery?.callConfirmed ?? false,
  )
  /** Shown only after a save attempt -- a blank form is not wrong until
      someone tries to save it. Same REQUIRED list in both modes, so a
      field cannot be cleared to blank in edit that Add would have
      rejected. */
  const [errors, setErrors] = useState<Partial<Record<RequiredField, string>>>(
    {},
  )
  const [panel, setPanel] = useState<Panel>('edit')
  const [cancelReason, setCancelReason] = useState('')
  /** Shown only after a confirm attempt -- same reasoning as `errors` above. */
  const [cancelReasonError, setCancelReasonError] = useState<string | null>(
    null,
  )
  /** Opt-in, defaults off: cancelling still discards edits unless the user
      asks otherwise. Interim until the business decides whether a cancel
      should keep or drop pending edits by default. */
  const [saveEditsOnCancel, setSaveEditsOnCancel] = useState(false)
  /** Add mode only: the add isn't optimistic, so the form waits for
      `onCreate` to settle before closing -- see `handleSubmit`. */
  const [saving, setSaving] = useState(false)
  /** Shown only once a save attempt actually fails. */
  const [submitError, setSubmitError] = useState<string | null>(null)
  /** The Cancel panel isn't optimistic either -- see `onCancelDelivery`. */
  const [cancelSaving, setCancelSaving] = useState(false)
  /** Shown only once a cancel attempt actually fails. */
  const [cancelError, setCancelError] = useState<string | null>(null)
  const formRef = useRef<HTMLFormElement>(null)
  /** The panel swap unmounts whichever button had focus, so Radix's focus
      trap falls back to the dialog container -- fine for AT, but a keyboard
      user has to Tab back in. Returning to the edit panel says where focus
      should land -- Back to the entry button, a validation bounce to the
      first invalid field (validateForm's own focus() ran while that field
      was still unmounted) -- and the effect below applies it. */
  const cancelEntryButtonRef = useRef<HTMLButtonElement>(null)
  const focusOnEditRef = useRef<'cancel-entry' | 'first-invalid' | null>(null)
  useEffect(() => {
    if (panel !== 'edit' || !focusOnEditRef.current) return
    const target = focusOnEditRef.current
    focusOnEditRef.current = null
    if (target === 'first-invalid') {
      // DOM order is the form's reading order, same as REQUIRED's.
      formRef.current
        ?.querySelector<HTMLElement>('[aria-invalid="true"]')
        ?.focus()
    } else {
      cancelEntryButtonRef.current?.focus()
    }
  }, [panel])
  // The awaited onCreate can resolve after the dialog has already closed
  // (e.g. Deliveries.tsx unmounts it on an unrelated state change) -- guard
  // against calling setState on an unmounted component. Re-armed on mount,
  // not just initialized true, so StrictMode's mount -> cleanup -> mount
  // doesn't leave it permanently false after the first cleanup.
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  /** Cancel panel's fields, back to their just-opened state -- shared by the
      delivery-swap effect and Back, so a third field can't drift out of
      sync between them. */
  function resetCancelPanel() {
    setCancelReason('')
    setCancelReasonError(null)
    setSaveEditsOnCancel(false)
    setCancelError(null)
  }

  // A different delivery in the same mounted dialog starts fresh.
  useEffect(() => {
    if (!delivery) return
    setVendor(delivery.vendor)
    setBpo(delivery.bpoNumber)
    setSupplierOrder(delivery.supplierOrderNumber ?? '')
    setCarrier(delivery.carrier)
    setDate(toDayKey(delivery.scheduledFor))
    setEta(toClockTime(delivery.scheduledFor))
    setBol(delivery.bolNumber ?? '')
    setReceiver(delivery.driver)
    setDestination(delivery.destination)
    setMaterial(delivery.material)
    setNote(delivery.note ?? '')
    setCallConfirmed(delivery.callConfirmed ?? false)
    setPanel('edit')
    resetCancelPanel()
  }, [delivery])

  /* Warn, don't block: a load arranged by phone and recorded after the fact
     is a real case (the rail's "Just arrived" exists for the same reason). */
  const pastDate = mode === 'add' && date !== '' && date < dayKeyOf(new Date())

  function clearError(field: RequiredField) {
    setErrors((current) =>
      current[field] ? { ...current, [field]: undefined } : current,
    )
  }

  function validateForm(): boolean {
    const values: Record<RequiredField, string> = {
      vendor,
      bpo,
      date,
      eta,
      destination,
      material,
    }
    const found: Partial<Record<RequiredField, string>> = {}
    for (const { field, message } of REQUIRED) {
      if (values[field].trim() === '') found[field] = message
    }
    setErrors(found)

    const first = REQUIRED.find(({ field }) => found[field])
    if (first) {
      formRef.current?.querySelector<HTMLElement>(`#${first.id}`)?.focus()
      return false
    }
    return true
  }

  /** Absolute values, only for what changed against `current` -- the service
      PATCH stays a minimal, idempotent statement of intent. Shared by the
      edit-mode save and the Cancel panel's "also save my edits" checkbox, so
      the two can't disagree about what counts as a pending edit. */
  function buildChanges(current: DeliveryDto): DeliveryUpdate {
    const changes: DeliveryUpdate = {}
    const scheduledFor = withClockTime(`${date}T00:00`, eta)
    if (carrier.trim() !== current.carrier) changes.carrier = carrier.trim()
    if (scheduledFor !== current.scheduledFor) {
      changes.scheduledFor = scheduledFor
    }
    // TODO: `undefined` here means "clear" only in memory -- see the note
    // on DeliveryUpdate in deliveryService.ts before this reaches a real API.
    if (bol.trim() !== (current.bolNumber ?? '')) {
      changes.bolNumber = bol.trim() || undefined
    }
    if (receiver !== current.driver) changes.driver = receiver
    if (destination !== current.destination) {
      changes.destination = destination
    }
    if (material.trim() !== current.material) {
      changes.material = material.trim()
    }
    if (note.trim() !== (current.note ?? '')) {
      changes.note = note.trim() || undefined
    }
    if (callConfirmed !== (current.callConfirmed ?? false)) {
      changes.callConfirmed = callConfirmed
    }
    return changes
  }

  /** Cancelling should not silently drop edits made before "Cancel this
      delivery" was clicked -- see the checkbox in the Cancel panel below.
      Interim fix pending a business decision on the default behavior. */
  const pendingChanges = delivery ? buildChanges(delivery) : {}
  const hasPendingChanges = Object.keys(pendingChanges).length > 0

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()

    if (mode === 'add') {
      if (!validateForm()) return
      const input: DeliveryCreate = {
        vendor: vendor.trim(),
        bpoNumber: bpo.trim(),
        carrier: carrier.trim(),
        material: material.trim(),
        destination: destination.trim(),
        scheduledFor: withClockTime(`${date}T00:00`, eta),
        // Nobody assigned is a state, not a gap -- the fixtures say so too.
        driver: receiver.trim() || 'Unassigned',
        supplierOrderNumber: supplierOrder.trim() || undefined,
        bolNumber: bol.trim() || undefined,
        note: note.trim() || undefined,
        callConfirmed: callConfirmed || undefined,
      }
      // Not optimistic: the row doesn't exist until the service assigns its
      // id, so the dialog waits here too -- on failure every value stays
      // intact and the user retries with one click instead of retyping.
      setSaving(true)
      setSubmitError(null)
      try {
        await onCreate(input)
        if (mountedRef.current) onClose()
      } catch (error: unknown) {
        log.error('Failed to add delivery', { input, error })
        if (mountedRef.current) {
          setSubmitError('The delivery could not be added. Try again.')
        }
        errorToast(`Could not add ${vendor.trim() || 'the delivery'}`, {
          detail: errorDetail(error),
        })
      } finally {
        if (mountedRef.current) setSaving(false)
      }
      return
    }

    if (!delivery || readOnly) return
    if (!validateForm()) return

    // Not optimistic: the tile shouldn't change until the write actually
    // lands, so the dialog waits here the same way Add and Cancel do.
    setSaving(true)
    setSubmitError(null)
    try {
      await onSave(delivery.id, buildChanges(delivery))
      if (mountedRef.current) onClose()
    } catch (error: unknown) {
      log.error('Failed to save delivery changes', { id: delivery.id, error })
      if (mountedRef.current) {
        setSubmitError('The changes could not be saved. Try again.')
      }
      errorToast(`Could not save changes to ${delivery.id}`, {
        detail: errorDetail(error),
      })
    } finally {
      if (mountedRef.current) setSaving(false)
    }
  }

  /** The message under a field, wired to it for assistive tech. Always
      mounted -- see .delivery-edit__field-error's min-height -- so a field
      erroring doesn't grow the dialog; aria-hidden while empty keeps the
      placeholder out of the accessibility tree. */
  function fieldError(field: RequiredField, id: string) {
    const message = errors[field]
    return (
      <p
        id={`${id}-error`}
        className="delivery-edit__field-error delivery-edit__field-error--slot"
        aria-hidden={message ? undefined : true}
      >
        {message}
      </p>
    )
  }

  /** Same reserved line as fieldError, for fields that never have a
      validation message -- so every field's row lines up instead of only
      the required ones growing a line. A span, not a <p>: several of these
      sit inside a <label>, which only permits phrasing content. */
  function fieldErrorSlot() {
    return (
      <span
        className="delivery-edit__field-error delivery-edit__field-error--slot"
        aria-hidden="true"
      />
    )
  }

  function describedBy(field: RequiredField, id: string) {
    return errors[field] ? `${id}-error` : undefined
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && !saving && !cancelSaving && onClose()}
    >
      <DialogContent className="sm:max-w-xl">
        <form
          ref={formRef}
          onSubmit={handleSubmit}
          noValidate
          className="delivery-edit__form grid gap-2"
        >
          {panel === 'cancel' && delivery ? (
            <>
              <DialogHeader>
                <DialogDescription className="delivery-edit__eyebrow">
                  Cancel delivery
                </DialogDescription>
                <DialogTitle className="delivery-edit__title flex items-center gap-2 text-(--dh-maroon)">
                  <X aria-hidden="true" className="size-5 shrink-0" />
                  Cancel this delivery
                </DialogTitle>
              </DialogHeader>

              <div className={labelClassName}>
                <label
                  htmlFor="cancel-reason"
                  className="delivery-edit__required flex items-center gap-1.5"
                >
                  Reason
                </label>
                <Textarea
                  id="cancel-reason"
                  // The rule guards against stealing focus on page load;
                  // this is a drill-in step inside an already-focused
                  // dialog, where landing on Reason is expected.
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                  value={cancelReason}
                  onChange={(event) => {
                    setCancelReason(event.target.value)
                    if (cancelReasonError) setCancelReasonError(null)
                  }}
                  rows={3}
                  aria-required
                  aria-invalid={cancelReasonError ? true : undefined}
                  aria-describedby={
                    cancelReasonError ? 'cancel-reason-error' : undefined
                  }
                  placeholder="Why is this delivery being cancelled?"
                />
                {/* Always mounted, like fieldError, so the panel doesn't
                    grow when the message appears. */}
                <p
                  id="cancel-reason-error"
                  className="delivery-edit__field-error delivery-edit__field-error--slot"
                  aria-hidden={cancelReasonError ? undefined : true}
                >
                  {cancelReasonError}
                </p>
              </div>

              {hasPendingChanges ? (
                <label className="flex items-center gap-2 text-xs normal-case text-(--fg2)">
                  <input
                    type="checkbox"
                    className="size-4"
                    checked={saveEditsOnCancel}
                    onChange={(event) =>
                      setSaveEditsOnCancel(event.target.checked)
                    }
                    disabled={cancelSaving}
                  />
                  Also save the changes I made to this delivery
                </label>
              ) : null}

              {cancelError ? (
                <p role="alert" className="delivery-edit__field-error">
                  {cancelError}
                </p>
              ) : null}

              <DialogFooter className="sm:justify-between">
                <Button
                  type="button"
                  variant="outline"
                  disabled={cancelSaving}
                  onClick={() => {
                    focusOnEditRef.current = 'cancel-entry'
                    setPanel('edit')
                    resetCancelPanel()
                  }}
                >
                  Back
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  disabled={cancelSaving}
                  onClick={async () => {
                    if (!cancelReason.trim()) {
                      setCancelReasonError('Enter a reason for cancelling.')
                      return
                    }
                    // Checking the box shouldn't let a cleared required
                    // field slip through as an empty value just because it
                    // skips Save changes -- bounce back to the edit panel,
                    // which shows the same inline messages validateForm()
                    // would have (it sets errors either way).
                    if (
                      saveEditsOnCancel &&
                      hasPendingChanges &&
                      !validateForm()
                    ) {
                      focusOnEditRef.current = 'first-invalid'
                      setPanel('edit')
                      return
                    }
                    // Not optimistic: wait for the cancel to actually land
                    // before treating it as done, so a failure can be
                    // retried instead of silently reverting later. Kept
                    // edits ride in the same write -- all-or-nothing, so a
                    // failure leaves neither applied.
                    setCancelSaving(true)
                    setCancelError(null)
                    try {
                      await onCancelDelivery(
                        delivery.id,
                        cancelReason.trim(),
                        saveEditsOnCancel && hasPendingChanges
                          ? pendingChanges
                          : undefined,
                      )
                      if (mountedRef.current) onClose()
                    } catch (error: unknown) {
                      log.error('Failed to cancel delivery', {
                        id: delivery.id,
                        error,
                      })
                      if (mountedRef.current) {
                        setCancelError(
                          'The delivery could not be cancelled. Try again.',
                        )
                      }
                      errorToast(`Could not cancel ${delivery.id}`, {
                        detail: errorDetail(error),
                      })
                    } finally {
                      if (mountedRef.current) setCancelSaving(false)
                    }
                  }}
                >
                  {cancelSaving ? (
                    <Spinner size="sm" />
                  ) : (
                    <X aria-hidden="true" />
                  )}
                  {cancelSaving ? 'Cancelling…' : 'Cancel this delivery'}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              {/* Suggestions for the add form's free-text fields. Native, so a
              value not in the list is still a value -- a new supplier is the
              whole point of adding by hand. */}
              {mode === 'add' ? (
                <>
                  <datalist id="edit-vendor-options">
                    {vendors.map((name) => (
                      <option key={name} value={name} />
                    ))}
                  </datalist>
                  <datalist id="edit-carrier-options">
                    {carriers.map((name) => (
                      <option key={name} value={name} />
                    ))}
                  </datalist>
                  <datalist id="edit-receiver-options">
                    {receivers.map((name) => (
                      <option key={name} value={name} />
                    ))}
                  </datalist>
                  <datalist id="edit-destination-options">
                    {destinations.map((name) => (
                      <option key={name} value={name} />
                    ))}
                  </datalist>
                </>
              ) : null}

              <DialogHeader>
                <DialogDescription className="delivery-edit__eyebrow">
                  {mode === 'add'
                    ? 'Add delivery'
                    : readOnly
                      ? 'View'
                      : 'View / edit'}
                </DialogDescription>
                {delivery ? (
                  <>
                    {/* Order identity: immutable here. The vendor and PO were
                    fixed when purchasing cut the order; only the shipment
                    below churns. */}
                    <DialogTitle className="delivery-edit__title flex items-center gap-2 text-(--blattner-dark-blue)">
                      <Building2
                        aria-hidden="true"
                        className="size-5 shrink-0"
                      />
                      {delivery.vendor}
                    </DialogTitle>
                    <p className="delivery-edit__meta mono">
                      <span className="sr-only">Blattner PO </span>
                      BPO {delivery.bpoNumber}
                    </p>
                  </>
                ) : (
                  <DialogTitle className="delivery-edit__title flex items-center gap-2 text-(--blattner-dark-blue)">
                    <Building2 aria-hidden="true" className="size-5 shrink-0" />
                    New delivery
                  </DialogTitle>
                )}
              </DialogHeader>

              {/* Gated on the stage, not just the note: statusNote is shared
                  with the Transfer workflow (#124879), whose note must not
                  read as a cancellation. */}
              {delivery?.stage === 'Cancelled' && delivery.statusNote ? (
                <p className="delivery-edit__hint flex items-center gap-2 rounded-md bg-(--gray-bright) px-3 py-2">
                  <X
                    aria-hidden="true"
                    className="size-3.5 shrink-0 text-(--dh-maroon)"
                  />
                  <span>
                    <strong className="text-(--dh-maroon)">Cancelled:</strong>{' '}
                    <span>{delivery.statusNote}</span>
                  </span>
                </p>
              ) : null}

              {mode === 'add' ? (
                /* The identity, entered: what the header shows in edit mode. */
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className={labelClassName}>
                    <label
                      htmlFor="edit-vendor"
                      className="delivery-edit__required flex items-center gap-1.5"
                    >
                      <Building2
                        aria-hidden="true"
                        className="size-3.5 shrink-0 text-(--blattner-dark-blue)"
                      />
                      Vendor
                    </label>
                    <Input
                      id="edit-vendor"
                      list="edit-vendor-options"
                      aria-required
                      value={vendor}
                      onChange={(event) => {
                        setVendor(event.target.value)
                        clearError('vendor')
                      }}
                      aria-invalid={errors.vendor ? true : undefined}
                      aria-describedby={describedBy('vendor', 'edit-vendor')}
                      placeholder="Who sold the material"
                      autoComplete="off"
                    />
                    {fieldError('vendor', 'edit-vendor')}
                  </div>
                  <div className={labelClassName}>
                    <label
                      htmlFor="edit-bpo"
                      className="delivery-edit__required flex items-center gap-1.5"
                    >
                      <Hash
                        aria-hidden="true"
                        className="size-3.5 shrink-0 text-(--blattner-dark-blue)"
                      />
                      Blattner PO
                    </label>
                    <Input
                      id="edit-bpo"
                      className="mono"
                      aria-required
                      value={bpo}
                      onChange={(event) => {
                        setBpo(event.target.value)
                        clearError('bpo')
                      }}
                      aria-invalid={errors.bpo ? true : undefined}
                      aria-describedby={describedBy('bpo', 'edit-bpo')}
                      placeholder="1142-54101491-004"
                      autoComplete="off"
                    />
                    {fieldError('bpo', 'edit-bpo')}
                  </div>
                </div>
              ) : delivery?.source === 'extracted' && !readOnly ? (
                <p className="delivery-edit__hint flex items-center gap-2">
                  <Sparkles aria-hidden="true" className="size-3.5 shrink-0" />
                  Extracted automatically — edit anything that needs correcting.
                </p>
              ) : null}

              <div className={labelClassName}>
                {/* Same icon language as the card: truck = who hauls it. */}
                <label
                  htmlFor="edit-carrier"
                  className="flex items-center gap-1.5"
                >
                  <Truck
                    aria-hidden="true"
                    className="size-3.5 shrink-0 text-(--blattner-dark-blue)"
                  />
                  Carrier
                </label>
                <Input
                  id="edit-carrier"
                  list={mode === 'add' ? 'edit-carrier-options' : undefined}
                  value={carrier}
                  onChange={(event) => setCarrier(event.target.value)}
                  disabled={readOnly}
                  autoComplete="off"
                />
                {fieldErrorSlot()}
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                <div className={labelClassName}>
                  <label
                    htmlFor="edit-date"
                    className="delivery-edit__required"
                  >
                    Delivery date
                  </label>
                  <Input
                    id="edit-date"
                    type="date"
                    aria-required
                    value={date}
                    onChange={(event) => {
                      setDate(event.target.value)
                      clearError('date')
                    }}
                    disabled={readOnly}
                    required
                    aria-invalid={errors.date ? true : undefined}
                    aria-describedby={
                      describedBy('date', 'edit-date') ??
                      (pastDate ? 'edit-date-past' : undefined)
                    }
                  />
                  {fieldError('date', 'edit-date')}
                </div>
                <div className={labelClassName}>
                  <label htmlFor="edit-eta" className="delivery-edit__required">
                    ETA
                  </label>
                  <Input
                    id="edit-eta"
                    type="time"
                    aria-required
                    value={eta}
                    onChange={(event) => {
                      setEta(event.target.value)
                      clearError('eta')
                    }}
                    disabled={readOnly}
                    required
                    aria-invalid={errors.eta ? true : undefined}
                    aria-describedby={describedBy('eta', 'edit-eta')}
                  />
                  {fieldError('eta', 'edit-eta')}
                </div>
                {/* w-fit shrinks the cell to the title's width, so centring the
                box centres it under the title rather than the column. */}
                <div className={`${labelClassName} w-fit`}>
                  <label
                    htmlFor="edit-call-confirmed"
                    className="flex items-center gap-1.5"
                  >
                    <CalendarCheck
                      aria-hidden="true"
                      className="size-3.5 shrink-0 text-(--fg-success)"
                    />
                    Confirmed
                  </label>
                  {/* h-9 matches the Input height, so the box centres on the
                  same line as the neighbouring fields' values. */}
                  <div className="flex h-9 items-center justify-center">
                    <input
                      id="edit-call-confirmed"
                      type="checkbox"
                      className="size-4"
                      checked={callConfirmed}
                      onChange={(event) =>
                        setCallConfirmed(event.target.checked)
                      }
                      disabled={readOnly}
                    />
                  </div>
                  {fieldErrorSlot()}
                </div>
              </div>

              {pastDate ? (
                <p
                  id="edit-date-past"
                  className="delivery-edit__hint flex items-center gap-2 rounded-md bg-(--gray-bright) px-3 py-2"
                >
                  <Sparkles aria-hidden="true" className="size-3.5 shrink-0" />
                  This date has already passed — saving records the delivery
                  after the fact.
                </p>
              ) : null}

              <div className="grid gap-3 sm:grid-cols-2">
                <label htmlFor="edit-bol" className={labelClassName}>
                  BOL on shipment
                  <Input
                    id="edit-bol"
                    className="mono"
                    value={bol}
                    onChange={(event) => setBol(event.target.value)}
                    disabled={readOnly}
                    placeholder="Arrives with the carrier's paperwork"
                  />
                  {fieldErrorSlot()}
                </label>
                {mode === 'add' ? (
                  <label
                    htmlFor="edit-supplier-order"
                    className={labelClassName}
                  >
                    Supplier order number
                    <Input
                      id="edit-supplier-order"
                      className="mono"
                      value={supplierOrder}
                      onChange={(event) => setSupplierOrder(event.target.value)}
                      placeholder="From the vendor's confirmation"
                      autoComplete="off"
                    />
                    {fieldErrorSlot()}
                  </label>
                ) : null}
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <label htmlFor="edit-receiver" className={labelClassName}>
                  <span className="flex items-center gap-1.5">
                    <UserRound
                      aria-hidden="true"
                      className="size-3.5 shrink-0 text-(--blattner-dark-blue)"
                    />
                    Assigned to receive the truck
                  </span>
                  {mode === 'add' ? (
                    <Input
                      id="edit-receiver"
                      list="edit-receiver-options"
                      value={receiver}
                      onChange={(event) => setReceiver(event.target.value)}
                      placeholder="Unassigned"
                      autoComplete="off"
                    />
                  ) : (
                    <select
                      id="edit-receiver"
                      className={selectClassName}
                      value={receiver}
                      onChange={(event) => setReceiver(event.target.value)}
                      disabled={readOnly}
                    >
                      {receivers.map((person) => (
                        <option key={person} value={person}>
                          {person}
                        </option>
                      ))}
                    </select>
                  )}
                  {fieldErrorSlot()}
                </label>
                <div className={labelClassName}>
                  <label
                    htmlFor="edit-destination"
                    className="delivery-edit__required flex items-center gap-1.5"
                  >
                    <MapPin
                      aria-hidden="true"
                      className="size-3.5 shrink-0 text-(--blattner-dark-blue)"
                    />
                    Destination
                  </label>
                  {mode === 'add' ? (
                    <Input
                      id="edit-destination"
                      list="edit-destination-options"
                      aria-required
                      value={destination}
                      onChange={(event) => {
                        setDestination(event.target.value)
                        clearError('destination')
                      }}
                      aria-invalid={errors.destination ? true : undefined}
                      aria-describedby={describedBy(
                        'destination',
                        'edit-destination',
                      )}
                      placeholder="Where the load unloads"
                      autoComplete="off"
                    />
                  ) : (
                    <select
                      id="edit-destination"
                      className={selectClassName}
                      aria-required
                      required
                      value={destination}
                      onChange={(event) => {
                        setDestination(event.target.value)
                        clearError('destination')
                      }}
                      disabled={readOnly}
                      aria-invalid={errors.destination ? true : undefined}
                      aria-describedby={describedBy(
                        'destination',
                        'edit-destination',
                      )}
                    >
                      {/* Only a record saved before this was required can be
                      blank; it must pick a destination before it can save. */}
                      {destination === '' ? (
                        <option value="" disabled>
                          Choose a destination
                        </option>
                      ) : null}
                      {destinations.map((yard) => (
                        <option key={yard} value={yard}>
                          {yard}
                        </option>
                      ))}
                    </select>
                  )}
                  {fieldError('destination', 'edit-destination')}
                </div>
              </div>

              <div className={labelClassName}>
                {/* "Summary" is load-bearing: this must never read as the BOM or
                the packing list -- those live with Receiving and JDE. Box
                icon matches the card's material line. */}
                <label
                  htmlFor="edit-material"
                  className="delivery-edit__required flex items-center gap-1.5"
                >
                  <Package
                    aria-hidden="true"
                    className="size-3.5 shrink-0 text-(--blattner-dark-blue)"
                  />
                  Material summary
                </label>
                <Input
                  id="edit-material"
                  aria-required
                  value={material}
                  onChange={(event) => {
                    setMaterial(event.target.value)
                    clearError('material')
                  }}
                  disabled={readOnly}
                  aria-invalid={errors.material ? true : undefined}
                  aria-describedby={describedBy('material', 'edit-material')}
                  placeholder="What is on the truck — a description, not a manifest"
                />
                {fieldError('material', 'edit-material')}
              </div>

              <label htmlFor="edit-note" className={labelClassName}>
                <span className="flex items-center gap-1.5">
                  <NotebookPen
                    aria-hidden="true"
                    className="size-3.5 shrink-0 text-(--blattner-dark-blue)"
                  />
                  Note
                </span>
                <Textarea
                  id="edit-note"
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  disabled={readOnly}
                  rows={2}
                  placeholder="Anything the yard should know"
                />
                {fieldErrorSlot()}
              </label>

              {delivery?.source === 'extracted' ? (
                <p className="delivery-edit__hint flex items-center gap-2 rounded-md bg-(--gray-bright) px-3 py-2">
                  <Mail aria-hidden="true" className="size-3.5 shrink-0" />
                  Original message attached
                  {delivery.bolNumber ? (
                    <span className="mono">· {delivery.bolNumber}</span>
                  ) : null}
                  {/* Link lands when the document store exists. */}
                </p>
              ) : null}

              {submitError ? (
                <p role="alert" className="delivery-edit__field-error">
                  {submitError}
                </p>
              ) : null}

              <DialogFooter className="sm:justify-between">
                {/* Cancelling is office work on a truck that never arrived --
                the state machine's only route is ETA -> Cancelled. Nothing to
                cancel on a delivery that does not exist yet. */}
                {delivery && !readOnly && isAwaitingArrival(delivery.stage) ? (
                  <Button
                    ref={cancelEntryButtonRef}
                    type="button"
                    variant="ghost"
                    className="text-(--dh-maroon) hover:text-(--dh-maroon)"
                    // saveEditsOnCancel is already false here -- Back and
                    // the delivery-swap effect both reset it on the way out
                    // of the cancel panel, so there's no stale value to
                    // clear on the way back in.
                    onClick={() => setPanel('cancel')}
                  >
                    <X aria-hidden="true" />
                    Cancel this delivery
                  </Button>
                ) : (
                  <span />
                )}
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onClose}
                    disabled={saving}
                  >
                    Close
                  </Button>
                  {/* Validation errors don't disable Add/Save -- the inline
                  messages are the feedback, and a disabled button explains
                  nothing. An in-flight save is different: disabling here
                  only guards against a double-submit while it's pending. */}
                  {mode === 'add' ? (
                    <Button type="submit" disabled={saving}>
                      {saving ? <Spinner size="sm" /> : null}
                      {saving ? 'Adding…' : 'Add delivery'}
                    </Button>
                  ) : !readOnly ? (
                    <Button type="submit" disabled={saving}>
                      {saving ? <Spinner size="sm" /> : null}
                      {saving ? 'Saving…' : 'Save changes'}
                    </Button>
                  ) : null}
                </div>
              </DialogFooter>
            </>
          )}
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default DeliveryEditDialog
